import { useEffect, useRef, useState } from "react"
import { useLocation, useNavigate } from "react-router-dom"
import { useEntidad, useManagedContext } from "../../components/hooks"
import { ServerRequest } from "../../utils/apiweb"
import { ALERT_TYPE, REQUEST_METHOD, ShowToastMessage } from '@laramie/ui';
import { APIS } from "../../config/apis"
import { SectionHeading, TableCustom, WrapperAdvancedSearch } from "../../components/common"
import { onRequestError, onRequestNoSuccess } from "../../utils/requests"
import { getDateToString, getFormatNumber, truncateTime } from "../../utils/convert"
import { useReporte } from "../../components/hooks/useReporte"
import { OpenObjectURL } from "../../utils/helpers"
import RegistrosContablesGridModal from "../../components/controls/RegistrosContablesGridModal"
import InformeRecaudacionConsolidadaModal from "../../components/controls/InformeRecaudacionConsolidadaModal"

const initItem = {
    id: 0
}

const RendicionRegistroContableHistorialView = () => {
    const { setIsLoading, showMessageModal } = useManagedContext()
    const location = useLocation()
    const navigate = useNavigate()

    const volverAlVep = location.state?.volverAlVep

    const searchRef = useRef()

    const [lotes, setLotes] = useState([])
    const [editItem, setEditItem] = useState(initItem)
    const [showDetalle, setShowDetalle] = useState(false)
    const [showInformeConsolidado, setShowInformeConsolidado] = useState(false)
    const [pathArchivoRegistroContable, setPathArchivoRegistroContable] = useState(null)
    const [bufferArchivoRegistroContable, setBufferArchivoRegistroContable] = useState(null)

    const [, getRowEntidad] = useEntidad({
        entidades: ['Recaudadora'],
        onLoaded: (_, isSuccess, error) => {
            if (!isSuccess) ShowToastMessage(ALERT_TYPE.ALERT_ERROR, error)
        },
        memo: {
            key: 'Recaudadora',
            timeout: 0
        }
    })


    useEffect(() => SearchLotes(), [])

    useEffect(() => {
        if (bufferArchivoRegistroContable) {
            OpenObjectURL(pathArchivoRegistroContable??"ArchivoRegistroContable.txt", bufferArchivoRegistroContable);
            setPathArchivoRegistroContable(null)
            setBufferArchivoRegistroContable(null)
        }
    }, [bufferArchivoRegistroContable])

    const [ generateReporte, ] = useReporte({
        callback: (reporte, buffer, message) => {
            if (buffer) {
                if (reporte === "ArchivoRegistroContable") {
                    setBufferArchivoRegistroContable(buffer);
                }
                else if (reporte === "InformeRecaudacionConsolidada") {
                    OpenObjectURL(`InformeConsolidado.pdf`, buffer);
                }
                else {
                    OpenObjectURL(`${reporte}.pdf`, buffer);
                }
            }
            else {
                ShowToastMessage(ALERT_TYPE.ALERT_ERROR, message)
            }
        },
        mode: 'blob'
    })

    const filterData = data => {
        const { numeroLote, fechaProcesoDesde,  fechaProcesoHasta} = searchRef.current.filters

        let filtered = [...data]

        if (numeroLote !== '') filtered = filtered.filter(item => item.numeroLote.indexOf(numeroLote) >= 0)
        if (fechaProcesoDesde) filtered = filtered.filter(item => truncateTime(item.fechaProceso).getTime() >= truncateTime(fechaProcesoDesde).getTime())
        if (fechaProcesoHasta) filtered = filtered.filter(item => truncateTime(item.fechaProceso).getTime() <= truncateTime(fechaProcesoHasta).getTime())

        return filtered
    }

    const SearchLotes = () => {
        setIsLoading(true)

        ServerRequest(
            REQUEST_METHOD.GET,
            null,
            true,
            APIS.URLS.REGISTRO_CONTABLE_LOTE,
            null,
            null,
            res => res.json().then(data => {
                data.forEach(row => {
                    if (row.fechaLote) row.fechaLote = new Date(row.fechaLote);
                    if (row.fechaProceso) row.fechaProceso = new Date(row.fechaProceso);
                    if (row.fechaControl) row.fechaControl = new Date(row.fechaControl);
                    if (row.fechaConciliacion) row.fechaConciliacion = new Date(row.fechaConciliacion);
                    if (row.fechaAcreditacion) row.fechaAcreditacion = new Date(row.fechaAcreditacion);
                })
                data.sort((a,b) => b.id - a.id)
                setLotes(filterData(data))
                setIsLoading(false)
            }),
            res => {
                onRequestNoSuccess(res)
                setIsLoading(false)
            },
            err => {
                onRequestError(err)
                setIsLoading(false)
            },
        )
    }

    const emitirInformeRedo = (id) => {
        const paramsReporte = {
            idRegistroContableLote: id
        }
        generateReporte("InformeRedo", paramsReporte);
    }

    const emitirInformeRecaudacionConsolidada = (fecha) => {
        const paramsReporte = {
            fecha
        }
        generateReporte("InformeRecaudacionConsolidada", paramsReporte);
    }

    const emimtirArchivoRegistroContable = (id) => {
        const paramsReporte = {
            idRegistroContableLote: id
        }
        generateReporte("ArchivoRegistroContable", paramsReporte);
    }

    const avmr = {
        onView: (item) => {
            setEditItem(item)
            setShowDetalle(true)
        },
        customActions: [
            {
                icon: 'download',
                title: 'Descargar archivo contable',
                onClick: (item) => {
                    setPathArchivoRegistroContable(item.pathArchivoRegistroContable)
                    emimtirArchivoRegistroContable(item.id)
                },
                requiresEdit: true
            },
            {
                icon: 'description',
                title: 'Descargar informe',
                onClick: ({ id }) => {
                    emitirInformeRedo(id)
                },
                requiresEdit: true
            }
        ],
    }


    const onModalCancel = () => {
        setShowDetalle(false)
        setEditItem(initItem)
    }


    return <>
        <SectionHeading titles={[{ title: 'Rendiciones' }, { title: 'Historial de rendición a contaduría' }]} />

        <section className='section-accordion'>
            <WrapperAdvancedSearch
                ref={searchRef}
                filters={[
                    { title: 'N° lote', field: 'numeroLote', type: 'string', size: 4, searchOnEnter: true, presetValue: location.state?.numeroLote },
                    { title: 'Fecha proceso desde', field: 'fechaProcesoDesde', type: 'date', size: 4 },
                    { title: 'Fecha proceso hasta', field: 'fechaProcesoHasta', type: 'date', size: 4, minValueField: 'fechaProcesoDesde' },
                ]}
                onSearch={SearchLotes}
            />

            <TableCustom
                className={'TableCustomBase m-top-20'}
                data={lotes}
                avmr={avmr}
                columns={[
                    { Header: 'N° lote', accessor: 'numeroLote', width: '14%', },
                    { Header: 'Fecha lote', accessor: 'fechaLote', Cell: (data) => getDateToString(data.value), width: '10%'},
                    { Header: 'Fecha proceso', accessor: 'fechaProceso', Cell: (data) => getDateToString(data.value, true), width: '10%'},
                    { Header: 'Fecha confirmación', accessor: 'fechaConfirmacion', Cell: (data) => getDateToString(data.value, true), width: '11%' },
                    { Header: 'Registros', accessor: 'casos', Cell: (data) => getFormatNumber(data.value, 0), width: '8%', alignCell: 'right' },
                    { Header: 'Importe total', accessor: 'importeTotal', Cell: (data) => getFormatNumber(data.value, 2), width: '15%', alignCell: 'right' },
                    { Header: 'Recaudadora', accessor: 'idRecaudadoraPrincipal', Cell: ({ value }) => getRowEntidad('Recaudadora', value)?.nombre ?? '', width: '16%' },
                    { Header: 'Archivo contable', accessor: 'pathArchivoRegistroContable', width: '16%', }
                ]}
            />

            {showDetalle && (
                <RegistrosContablesGridModal
                    idRegistroContableLote={editItem.id}
                    onCancel={onModalCancel}
                />
            )}

            {showInformeConsolidado && (
                <InformeRecaudacionConsolidadaModal
                    onConfirm={(fecha) => { emitirInformeRecaudacionConsolidada(fecha); setShowInformeConsolidado(false) }}
                    onCancel={() => setShowInformeConsolidado(false)}
                />
            )}

        </section>

        <footer className='footer footer-action'>
            <div className='footer-action-container'>
                {volverAlVep && (<button className="btn back-button float-start" onClick={() => navigate(volverAlVep.to, { state: volverAlVep.state })}>Volver al VEP</button>)}
                <button className="btn action-button float-end" onClick={() => setShowInformeConsolidado(true)}>Informe consolidado</button>
            </div>
        </footer>
    </>
}

export default RendicionRegistroContableHistorialView
